import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Search, Download, Database, BookOpen, Sparkles, Trash2, Eye, Edit, CheckCircle } from 'lucide-react';
import { VehicleSpecDetail } from '../../components/VehicleSpecDetail';
import { SpecCollectorQueueWidget } from '../../components/SpecCollectorQueueWidget';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminKnowledgeNew = () => {
  const [activeTab, setActiveTab] = useState('knowledge');
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [vehicleSpecs, setVehicleSpecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAISearchModal, setShowAISearchModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // AI Search Form
  const [aiSearchForm, setAiSearchForm] = useState({
    brands: [''],
    models: [''],
    engine: '',
    auto_expand: true
  });

  useEffect(() => {
    if (activeTab === 'knowledge') {
      fetchKnowledge();
    } else if (activeTab === 'specs') {
      fetchVehicleSpecs();
    }
  }, [activeTab]);

  const fetchKnowledge = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/knowledge`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKnowledgeItems(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Knowledge Base');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleSpecs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/knowledge/vehicle-specs?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleSpecs(response.data.specs || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Fahrzeugdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'knowledge' 
        ? `${API}/admin/knowledge/knowledge-base/export/${format}`
        : `${API}/admin/knowledge/vehicle-specs/export/${format}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export erfolgreich (${format.toUpperCase()})`);
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const handleStartAISearch = async () => {
    try {
      const token = localStorage.getItem('token');
      const brands = aiSearchForm.brands.filter(b => b.trim());
      const models = aiSearchForm.models.filter(m => m.trim());
      
      // NEU: Nur Marke ist jetzt ausreichend!
      if (brands.length === 0) {
        toast.error('Bitte mindestens eine Marke eingeben');
        return;
      }
      
      // Wenn kein Modell angegeben, Info-Toast
      if (models.length === 0) {
        toast.info('Suche nach ALLEN Modellen der Marke(n)...');
      }

      await axios.post(`${API}/admin/knowledge/vehicle-specs/search`, {
        brands,
        models,
        engine: aiSearchForm.engine || null,
        auto_expand: aiSearchForm.auto_expand
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('KI-Suche gestartet! Die Daten werden im Hintergrund gesammelt.');
      setShowAISearchModal(false);
      
      // Refresh after a delay
      setTimeout(() => {
        fetchVehicleSpecs();
      }, 3000);
    } catch (error) {
      toast.error('KI-Suche konnte nicht gestartet werden');
    }
  };

  const handleViewDetail = async (specId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/knowledge/vehicle-specs/${specId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedItem(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    }
  };

  const handleDeleteSpec = async (specId) => {
    if (!window.confirm('Möchten Sie diese Fahrzeugdaten wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/knowledge/vehicle-specs/${specId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fahrzeugdaten gelöscht');
      fetchVehicleSpecs();
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleVerifySpec = async (specId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/knowledge/vehicle-specs/${specId}`, {
        verification_status: 'verified'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Fahrzeugdaten verifiziert');
      setShowDetailModal(false);
      fetchVehicleSpecs();
    } catch (error) {
      toast.error('Verifizierung fehlgeschlagen');
    }
  };

  const handleCompleteAllMissingData = async () => {
    if (!window.confirm('Möchten Sie alle unvollständigen Fahrzeugdaten vervollständigen?\n\nDies kann einige Zeit dauern. Die Fahrzeuge werden sequenziell verarbeitet (1 pro Minute).')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/knowledge/vehicle-specs/complete-missing-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        `✅ ${response.data.queued_count} Fahrzeuge zur Vervollständigung hinzugefügt!\n\nSie werden im Hintergrund verarbeitet (1 pro Minute).`,
        { duration: 5000 }
      );

      // Refresh specs after a delay
      setTimeout(() => {
        fetchVehicleSpecs();
      }, 2000);
    } catch (error) {
      toast.error('Fehler beim Starten der Datenvervollständigung');
      console.error('Complete missing data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBrandField = () => {
    setAiSearchForm(prev => ({ ...prev, brands: [...prev.brands, ''] }));
  };

  const addModelField = () => {
    setAiSearchForm(prev => ({ ...prev, models: [...prev.models, ''] }));
  };

  const updateBrand = (index, value) => {
    const newBrands = [...aiSearchForm.brands];
    newBrands[index] = value;
    setAiSearchForm(prev => ({ ...prev, brands: newBrands }));
  };

  const updateModel = (index, value) => {
    const newModels = [...aiSearchForm.models];
    newModels[index] = value;
    setAiSearchForm(prev => ({ ...prev, models: newModels }));
  };

  const filteredKnowledge = knowledgeItems.filter(item => 
    item.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.tags?.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.tags?.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSpecs = vehicleSpecs.filter(spec =>
    spec.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    spec.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    spec.engine?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          🧠 Wissensmanagement
        </h2>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="knowledge" className="flex items-center gap-2">
              <BookOpen size={16} />
              Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="specs" className="flex items-center gap-2">
              <Database size={16} />
              Fahrzeug-Spezifikationen
            </TabsTrigger>
          </TabsList>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle style={{ color: '#E6EDF3' }}>
                    Knowledge Items ({filteredKnowledge.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleExport('knowledge', 'csv')}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                    >
                      <Download size={16} className="mr-2" />
                      CSV
                    </Button>
                    <Button
                      onClick={() => handleExport('knowledge', 'json')}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                    >
                      <Download size={16} className="mr-2" />
                      JSON
                    </Button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3" size={16} style={{ color: '#94A3B8' }} />
                    <Input
                      placeholder="Suche nach Symptom, Marke, Modell..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p style={{ color: '#94A3B8' }}>Laden...</p>
                ) : filteredKnowledge.length === 0 ? (
                  <p style={{ color: '#94A3B8' }}>Keine Einträge gefunden.</p>
                ) : (
                  <div className="space-y-3">
                    {filteredKnowledge.map((item) => (
                      <div
                        key={item.id}
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#1EA0E620', color: '#7CD4FD' }}>
                                {item.source_type}
                              </span>
                              {item.tags?.brand && (
                                <span className="text-xs" style={{ color: '#94A3B8' }}>
                                  {item.tags.brand} {item.tags.model}
                                </span>
                              )}
                            </div>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>
                              {item.text?.substring(0, 200)}...
                            </p>
                            <div className="flex gap-4 mt-2 text-xs" style={{ color: '#94A3B8' }}>
                              <span>Score: {item.quality_score?.toFixed(2) || 0}</span>
                              <span>Nutzung: {item.usage_count || 0}</span>
                              <span>Erfolg: {item.success_count || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicle Specs Tab */}
          <TabsContent value="specs">
            {/* Queue Status Widget */}
            <div className="mb-6">
              <SpecCollectorQueueWidget onTaskComplete={fetchVehicleSpecs} />
            </div>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle style={{ color: '#E6EDF3' }}>
                    Fahrzeug-Spezifikationen ({filteredSpecs.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowAISearchModal(true)}
                      style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
                      size="sm"
                    >
                      <Sparkles size={16} className="mr-2" />
                      KI-Suche starten
                    </Button>
                    <Button
                      onClick={handleCompleteAllMissingData}
                      style={{ backgroundColor: '#10B981', color: '#FFFFFF' }}
                      size="sm"
                      disabled={loading}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Alle vervollständigen
                    </Button>
                    <Button
                      onClick={() => handleExport('specs', 'csv')}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                    >
                      <Download size={16} className="mr-2" />
                      CSV
                    </Button>
                    <Button
                      onClick={() => handleExport('specs', 'json')}
                      variant="outline"
                      size="sm"
                      style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                    >
                      <Download size={16} className="mr-2" />
                      JSON
                    </Button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3" size={16} style={{ color: '#94A3B8' }} />
                    <Input
                      placeholder="Suche nach Marke, Modell, Motor..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p style={{ color: '#94A3B8' }}>Laden...</p>
                ) : filteredSpecs.length === 0 ? (
                  <p style={{ color: '#94A3B8' }}>Noch keine Fahrzeugdaten vorhanden. Starten Sie eine KI-Suche!</p>
                ) : (
                  <div className="space-y-3">
                    {filteredSpecs.map((spec) => {
                      const completeness = (spec.completeness_score || 0) * 100;
                      const statusIcon = spec.verification_status === 'verified' ? '✅' : 
                                        spec.verification_status === 'unverified' ? '⚠️' : '❌';
                      
                      return (
                        <div
                          key={spec.id}
                          className="p-4 rounded-lg"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg font-semibold" style={{ color: '#E6EDF3' }}>
                                  {spec.brand} {spec.model}
                                </span>
                                <span className="text-sm">{statusIcon}</span>
                              </div>
                              {spec.engine && (
                                <p className="text-sm" style={{ color: '#94A3B8' }}>
                                  Motor: {spec.engine}
                                </p>
                              )}
                              {spec.years && spec.years.length > 0 && (
                                <p className="text-xs" style={{ color: '#94A3B8' }}>
                                  Jahre: {spec.years.join(', ')}
                                </p>
                              )}
                              <div className="mt-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                                    <div
                                      className="h-2 rounded-full"
                                      style={{
                                        width: `${completeness}%`,
                                        backgroundColor: completeness >= 80 ? '#22C55E' : completeness >= 50 ? '#F59E0B' : '#EF4444'
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs" style={{ color: '#94A3B8' }}>
                                    {completeness.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                onClick={() => handleViewDetail(spec.id)}
                                variant="outline"
                                size="sm"
                                style={{ borderColor: '#1F2937', color: '#7CD4FD' }}
                              >
                                <Eye size={16} />
                              </Button>
                              <Button
                                onClick={() => handleDeleteSpec(spec.id)}
                                variant="outline"
                                size="sm"
                                style={{ borderColor: '#1F2937', color: '#EF4444' }}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* AI Search Modal */}
        <Dialog open={showAISearchModal} onOpenChange={setShowAISearchModal}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', maxWidth: '600px' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>
                <Sparkles className="inline mr-2" size={20} />
                KI-gestützte Fahrzeugdaten-Suche
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: '#94A3B8' }}>
                  Marke(n)
                </label>
                {aiSearchForm.brands.map((brand, index) => (
                  <Input
                    key={index}
                    placeholder="z.B. BMW, VW, Mercedes"
                    value={brand}
                    onChange={(e) => updateBrand(index, e.target.value)}
                    className="mb-2"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                ))}
                <Button
                  onClick={addBrandField}
                  variant="outline"
                  size="sm"
                  style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                >
                  + Weitere Marke
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: '#94A3B8' }}>
                  Modell(e) <span style={{ color: '#7CD4FD' }}>(optional - leer lassen für ALLE Modelle)</span>
                </label>
                {aiSearchForm.models.map((model, index) => (
                  <Input
                    key={index}
                    placeholder="z.B. E90, Golf, C-Klasse"
                    value={model}
                    onChange={(e) => updateModel(index, e.target.value)}
                    className="mb-2"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                ))}
                <Button
                  onClick={addModelField}
                  variant="outline"
                  size="sm"
                  style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                >
                  + Weiteres Modell
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: '#94A3B8' }}>
                  Motor (optional)
                </label>
                <Input
                  placeholder="z.B. 2.0 TDI, N47D20"
                  value={aiSearchForm.engine}
                  onChange={(e) => setAiSearchForm(prev => ({ ...prev, engine: e.target.value }))}
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={aiSearchForm.auto_expand}
                  onChange={(e) => setAiSearchForm(prev => ({ ...prev, auto_expand: e.target.checked }))}
                  id="auto-expand"
                />
                <label htmlFor="auto-expand" className="text-sm" style={{ color: '#94A3B8' }}>
                  Automatisch alle Varianten finden (z.B. Golf 1-8, GTI, R, etc.)
                </label>
              </div>

              <div className="p-3 rounded" style={{ backgroundColor: '#1EA0E620', borderColor: '#1EA0E6', border: '1px solid' }}>
                <p className="text-xs mb-2" style={{ color: '#7CD4FD' }}>
                  💡 <strong>Nur Marke angeben:</strong> Findet ALLE Modelle (z.B. &quot;BMW&quot; → 3er, 5er, X5, etc.)
                </p>
                <p className="text-xs" style={{ color: '#7CD4FD' }}>
                  ⏱️ Der Vorgang läuft im Hintergrund und kann mehrere Minuten dauern.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  onClick={() => setShowAISearchModal(false)}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleStartAISearch}
                  style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
                >
                  Suche starten
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Modal mit neuer Komponente */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', maxWidth: '1000px', maxHeight: '90vh', overflow: 'hidden' }}>
            {selectedItem && (
              <VehicleSpecDetail 
                spec={selectedItem}
                onVerify={handleVerifySpec}
                onClose={() => setShowDetailModal(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};